<template>
  <div class="columns">
    <div v-if="currentUserAccess('ProjectLogs',this.UserActions.CREATE,this.project_id)"
         class="column is-mobile mt-4">
      <b-collapse
          class="card"
          animation="slide"
          aria-id="contentIdForA11y3"
          :open="false">
        <template #trigger="props">
          <div
              class="card-header"
              role="button"
              aria-controls="contentIdForA11y3"
              :aria-expanded="props.open">
            <p class="card-header-title">
              {{ $t('project-logs.new-work-log-button') }}
            </p>
            <a class="card-header-icon">
              <b-icon
                  :icon="props.open ? 'menu-down' : 'menu-up'">
              </b-icon>
            </a>
          </div>
        </template>

        <div class="card-content">
          <div class="content">
            <b-loading :active="isLoading"></b-loading>
            <!--    Task                    -->
            <div class="columns">
              <div class="column is-4">
                <b-field required :label="$t('task-log-view.task')">
                  <b-taginput
                      maxtags="1"
                      v-model="tasksTags"
                      :data="filteredTasks"
                      :loading="false"
                      :has-counter="false"
                      autocomplete
                      :open-on-focus="true"
                      field="title"
                      icon="format-list-checks"
                      :remove-on-keys=[]
                      :placeholder="$t('task-log-view.task-placeholder')"
                      tabindex="2"
                      @typing="getFilteredTasks">
                  </b-taginput>
                </b-field>
              </div>
              <div class="column is-3">
                <div class="columns no-margin">
                  <div class="column is-12">
                    <b-field required :label="quantityTitle">
                      <b-numberinput :editable="true" tabindex="7" v-model="quantity" min-step="0.00001">
                      </b-numberinput>
                    </b-field>
                    <b-field style="margin-top: 10px;">
                      {{ remainingQuantity }}
                    </b-field>
                  </div>
                </div>
              </div>
              <div class="column is-3">
                <div class="columns no-margin">
                  <div class="column is-half">
                    <b-field :label="$t('enums.personal-group.internal')">
                      <p class="internal-text mt-2" style="width: 80px">
                        {{ getFormattedTimeHour(internalHoursSum) }}</p>
                    </b-field>
                  </div>
                  <div class="column is-half">
                    <b-field :label="$t('enums.personal-group.external')">
                      <p class="external-text mt-2" style="width: 80px">
                        {{ getFormattedTimeHour(externalHoursSum) }}</p>
                    </b-field>
                  </div>
                </div>
              </div>
            </div>
            <hr>
            <!--    Personnel                    -->
            <div class="columns" v-for="(personalEntry, index) in personnelLogs" :key="index.toString() + 'personal'">
              <div class="column is-4">
                <b-field required :label="index === 0 ? $t('fast-tasks-log.personal-list-title') : null">
                  <b-taginput
                      maxtags="1"
                      v-model="personalEntry.personnelTags"
                      :data="filteredPersonnel"
                      :loading="false"
                      :has-counter="false"
                      :open-on-focus="true"
                      autocomplete
                      field="fullname"
                      icon="account-box"
                      :remove-on-keys=[]
                      :placeholder="$t('personal-log-view.personal-placeholder')"
                      tabindex="2"
                      @typing="getFilteredPersonnel">
                  </b-taginput>
                </b-field>
              </div>
              <div class="column is-3">
                <b-field required :label="index === 0 ? $t('personal-log-view.hours') : null">
                  <b-numberinput :editable="true" tabindex="3" v-model="personalEntry.personalWorkingHour" min="0"
                                 step="1" max="24">
                  </b-numberinput>
                </b-field>
              </div>
              <div class="column is-3">
                <b-field required :label="index === 0 ? $t('personal-log-view.minutes') : null">
                  <b-numberinput :editable="false" tabindex="4" v-model="personalEntry.personalWorkingMinutes" min="0"
                                 max="45"
                                 step="15">
                  </b-numberinput>
                </b-field>
              </div>
              <div class="column is-1">
                <b-button v-if="index !== 0" class="button is-primary is-danger" icon-left="close-thick"
                          @click="removePersonalEntry(index)"/>
              </div>
            </div>
            <!--    Add Personnel Button         -->
            <b-button class="button is-primary is-mobile" icon-left="plus-thick"
                      @click="addPersonalEntry">
              {{ $t('fast-tasks-log.new-personal-log') }}
            </b-button>
            <hr>
            <!--    Equipments        -->
            <div class="columns" v-for="(equipmentEntry, index) in equipmentLogs"
                 :key="index.toString() + 'equipment'">
              <div class="column is-4">
                <b-field required :label="index === 0 ? $t('fast-tasks-log.equipment-list-title') : null">
                  <b-taginput
                      maxtags="1"
                      v-model="equipmentEntry.equipmentsTags"
                      :data="filteredEquipments"
                      :loading="false"
                      :has-counter="false"
                      :open-on-focus="true"
                      autocomplete
                      field="model"
                      icon="robot-industrial"
                      :remove-on-keys=[]
                      :placeholder="$t('equipment-log-view.equipment-placeholder')"
                      tabindex="2"
                      @typing="getFilteredEquipments"
                      @add="equipmentAdded"
                      @remove="equipmentRemoved">
                  </b-taginput>
                </b-field>
              </div>
              <div class="column is-3">
                <b-field required :label="index === 0 ? $t('equipment-log-view.days') : null">
                  <b-numberinput :editable="true" tabindex="3" v-model="equipmentEntry.quantityDay" min="0" step="1">
                  </b-numberinput>
                </b-field>
                <b-field style="margin-top: 10px;">
                  {{ remainingEquipmentQuantity(equipmentEntry) }}
                </b-field>
              </div>
              <div class="column is-1">
                <b-button v-if="index !== 0" class="button is-primary is-danger" icon-left="close-thick"
                          @click="removeEquipmentsEntry(index)"/>
              </div>
            </div>
            <!--    Add Equipment Button         -->
            <b-button class="button is-primary is-mobile" icon-left="plus-thick"
                      @click="addEquipmentEntry">
              {{ $t('fast-tasks-log.new-equipment-log') }}
            </b-button>
            <hr>
            <!--    Consumables        -->
            <div class="columns" v-for="(consumableEntry, index) in consumableLogs"
                 :key="index.toString() + 'consumable'">
              <div class="column is-4">
                <b-field required :label="index === 0 ? $t('fast-tasks-log.consumable-list-title') : null">
                  <b-taginput
                      maxtags="1"
                      v-model="consumableEntry.consumablesTags"
                      :data="filteredConsumables"
                      :loading="false"
                      :has-counter="false"
                      :open-on-focus="true"
                      autocomplete
                      field="resource"
                      icon="cart-variant"
                      :remove-on-keys=[]
                      :placeholder="$t('consumable-log-view.resource-placeholder')"
                      tabindex="2"
                      @typing="getFilteredConsumables"
                      @add="consumableAdded"
                      @remove="consumableRemoved">
                  </b-taginput>
                </b-field>
              </div>
              <div class="column is-3">
                <b-field required :label="index === 0 ? $t('consumable-log-view.quantity') : null">
                  <b-numberinput :editable="true" tabindex="3" v-model="consumableEntry.quantity" min="0"
                                  step="0.1">
                  </b-numberinput>
                </b-field>
                <b-field style="margin-top: 10px;">
                  {{ remainingConsumableQuantity(consumableEntry) }}
                  {{ consumableQuantityTitle(consumableEntry) }}
                </b-field>
              </div>
              <div class="column is-1">
                <b-button v-if="index !== 0" class="button is-primary is-danger" icon-left="close-thick" 
                          @click="removeConsumablesEntry(index)"/>
              </div>
            </div>
            <!--    Add Consumable Button         -->
            <b-button class="button is-primary is-mobile" icon-left="plus-thick"
                      @click="addConsumableEntry">
              {{ $t('fast-tasks-log.new-consumable-log') }}
            </b-button>
            <hr>
            <div class="columns">
              <div class="column is-7">
                <b-field :label="$t('task-log-view.comments')">
                  <b-input v-model="taskText"
                           :placeholder="$t('task-log-view.comments-placeholder')" tabindex="8"
                           type="textarea"></b-input>
                </b-field>
              </div>
            </div>
          </div>
        </div>
        <footer class="card-footer">
          <a class="card-footer-item" @click="onReset">{{ $t('common.reset') }}</a>
          <a class="card-footer-item" @click="onSave">{{ $t('common.save') }}</a>
        </footer>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import firebase from "firebase";

export default {
  name: "FastProjectTaskLog",
  props: {
    'project_id': {
      type: String,
    },
    'selected_date': {},
  },
  data() {
    return {
      isLoading: false,
      pendingOperations: 0,  // counter to track pending save operations
      tasksData: [],
      filteredTasks: [],
      tasksTags: [],
      quantity: 0.0,
      taskText: "",
      personnelData: [],
      filteredPersonnel: [],
      personalFullName: "",
      personnelLogs: [
        {
          personnelTags: [],
          personalWorkingHour: 0,
          personalWorkingMinutes: 0,
          personalGroup: ""
        }
      ],
      equipmentsData: [],
      filteredEquipments: [],
      equipmentLogs: [
        {
          equipmentsTags: [],
          quantityDay: 0,
        }
      ],
      consumablesData: [],
      filteredConsumables: [],
      consumableLogs: [
        {
          consumablesTags: [],
          quantity: 0,
        }
      ],
    }
  },
  methods: {
    readTasksData: function () {
      this.tasksData = [];
      const functions = firebase.app().functions("europe-west3");
      const getTasksFunctions = functions.httpsCallable(this.getSubmitEndpoint());
      getTasksFunctions({action: "get_tasks", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            console.log("tasks list result: ", result.data);
            if (result.data.tasks.length > 0) {
              result.data["tasks"].forEach((task) => {
                this.tasksData.push({
                  task: task,
                  title: task.title
                });
              });
            }
            this.filteredTasks = this.tasksData;
            console.log("tasksData: ", this.tasksData);
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting tasks: ", error);
          });
    },
    getFilteredTasks(text) {
      this.filteredTasks = this.tasksData.filter((option) => {
        return option.title
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    readPersonnelData: function () {
      this.personnelData = [];
      const functions = firebase.app().functions("europe-west3");
      const getPersonnelFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getPersonnelFunction({action: "get_personnel", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            console.log("personnel list result: ", result.data);
            if (result.data.personnel.length > 0) {
              result.data["personnel"].forEach((personal) => {
                this.personnelData.push({
                  personal: personal,
                  fullname: this.getFullName(personal.name),
                });
              });
            }
            this.filteredPersonnel = this.personnelData;
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting personnel: ", error);
          });
    },
    getFilteredPersonnel(text) {
      this.filteredPersonnel = this.personnelData.filter((option) => {
        return option.fullname
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    addPersonalEntry() {
      // Add a new entry with the same structure as above
      this.personnelLogs.push({
        personnelTags: [],
        personalWorkingHour: 0,
        personalWorkingMinutes: 0
      });
    },
    removePersonalEntry(index) {
      // Check if the index is within the bounds of the array
      if (index >= 0 && index < this.personnelLogs.length) {
        this.personnelLogs.splice(index, 1);
      }
    },
    removeEquipmentsEntry(index) {
      if (index >= 0 && index < this.equipmentLogs.length) {
        this.equipmentLogs.splice(index, 1);
      }
    },
    removeConsumablesEntry(index) {
      if (index >= 0 && index < this.consumableLogs.length) {
        this.consumableLogs.splice(index, 1);
      }
    },
    readEquipmentsData: function () {
      this.equipmentsData = [];
      const functions = firebase.app().functions("europe-west3");
      const getEquipmentsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getEquipmentsFunction({action: "get_equipments", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            console.log("equipments list result: ", result.data);
            if (result.data.equipments.length > 0) {
              result.data["equipments"].forEach((equipment) => {
                this.equipmentsData.push({
                  equipment: equipment,
                  model: equipment.model
                });
              });
            }
            this.filteredEquipments = this.equipmentsData;
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting equipments: ", error);
          });
    },
    getFilteredEquipments(text) {
      this.filteredEquipments = this.equipmentsData.filter((option) => {
        return option.equipment.model
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    equipmentAdded(tagEntry) {
      console.log("equipment added")
      console.dir(tagEntry)
      this.equipmentsData = this.equipmentsData.filter(item => item.model !== tagEntry.model);
      this.filteredEquipments = this.equipmentsData
    },
    equipmentRemoved(tagEntry) {
      console.log("equipment removed")
      console.dir(tagEntry)
      this.equipmentsData.push(tagEntry)
      this.filteredEquipments = this.equipmentsData
    },
    addEquipmentEntry() {
      this.equipmentLogs.push({
        equipmentsTags: [],
        quantityDay: 0,
      });
    },
    remainingEquipmentQuantity(equipmentEntry) {
      let tag;
      let totalQuantity = 0;
      let totalLoggedQuantity = 0;
      let curLogSavedQuantity = 0;
      if (equipmentEntry.equipmentsTags[0] === undefined || equipmentEntry.equipmentsTags[0] == null)
        return '';
      tag = equipmentEntry.equipmentsTags[0].equipment;
      if (tag.meta.estimation === undefined || tag.meta.estimation == null)
        return;
      totalLoggedQuantity = tag.logged_quantities;
      totalQuantity = tag.meta.estimation.quantity;
      let remaining = (totalLoggedQuantity - totalQuantity - curLogSavedQuantity + equipmentEntry.quantityDay).toFixed(3);
      return ((remaining > 0) ? this.$t('project-logs.exceeding') : this.$t('project-logs.remaining')) + ": " + Math.abs(remaining);
    },
    readConsumablesData: function () {
      this.consumablesData = [];
      const functions = firebase.app().functions("europe-west3");
      const getConsumablesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getConsumablesFunction({action: "get_consumables", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            console.log("consumables1 list result: ", result.data);
            if (result.data.consumables.length > 0) {
              result.data["consumables"].forEach((consumable) => {
                this.consumablesData.push({
                  consumable: consumable,
                  resource: consumable.resource
                });
              });
            }
            this.filteredConsumables = this.consumablesData;
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting consumables: ", error);
          });
    },
    getFilteredConsumables(text) {
      this.filteredConsumables = this.consumablesData.filter((option) => {
        return option.consumable.resource
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    consumableAdded(tagEntry) {
      this.consumablesData = this.consumablesData.filter(item => item.resource !== tagEntry.resource);
      this.filteredConsumables = this.consumablesData
    },
    consumableRemoved(tagEntry) {
      this.consumablesData.push(tagEntry)
      this.filteredConsumables = this.consumablesData
    },
    addConsumableEntry() {
      this.consumableLogs.push({
        consumablesTags: [],
        quantity: 0,
      });
    },
    consumableQuantityTitle(consumableLogEntry) {
      if (consumableLogEntry.consumablesTags[0] === undefined || consumableLogEntry.consumablesTags[0] == null
          || consumableLogEntry.consumablesTags[0].consumable.meta.estimation === undefined || consumableLogEntry.consumablesTags[0].consumable.meta.estimation == null) {
        return null;
      }
      let u = this.quantityUnits.find(obj => {
        return obj.id === consumableLogEntry.consumablesTags[0].consumable.unit
      });
      return ((u === undefined || u === "") ? "" : "(" + this.$t(u.translationKey) + ")");
    },
    remainingConsumableQuantity(consumableLogEntry) {
      let tag;
      let totalQuantity = 0;
      let totalLoggedQuantity = 0;
      let curLogSavedQuantity = 0;
      if (consumableLogEntry.consumablesTags[0] === undefined || consumableLogEntry.consumablesTags[0] == null)
        return '';
      tag = consumableLogEntry.consumablesTags[0].consumable;
      if (tag.meta.estimation === undefined || tag.meta.estimation == null)
        return;
      totalLoggedQuantity = tag.logged_quantities;
      totalQuantity = tag.meta.estimation.quantity;
      let remaining = (totalLoggedQuantity - totalQuantity - curLogSavedQuantity + consumableLogEntry.quantity).toFixed(3);
      return ((remaining > 0) ? this.$t('project-logs.exceeding') : this.$t('project-logs.remaining')) + ": " + Math.abs(remaining);
    },
    onReset() {
      this.tasksTags = [];
      this.quantity = 0.0;
      this.taskText = "";
      this.personnelLogs = [
        {
          personnelTags: [],
          personalWorkingHour: 0,
          personalWorkingMinutes: 0,
          personalGroup: ""
        }
      ];
      this.equipmentLogs = [
        {
          equipmentsTags: [],
          quantityDay: 0,
        }
      ];
      this.consumableLogs = [
        {
          consumablesTags: [],
          quantity: 0,
        }
      ];
    },
    validateTaskLogForm() {
      return (this.tasksTags != null && this.tasksTags.length > 0) && ((this.internalHoursSum + this.externalHoursSum) > 0)
    },
    validatePersonalLogForm(personalLog) {
      return personalLog.personnelTags != null && personalLog.personnelTags.length > 0
    },
    validateEquipmentLogForm(equipmentLog) {
      return (equipmentLog.quantityDay != null && equipmentLog.quantityDay !== 0) && (equipmentLog.equipmentsTags != null && equipmentLog.equipmentsTags.length > 0)
    },
    validateConsumableLogForm(consumableLog) {
      return (consumableLog.quantity != null && consumableLog.quantity !== 0) && (consumableLog.consumablesTags != null && consumableLog.consumablesTags.length > 0)
    },
    onSave() {
      if (!this.validateTaskLogForm()) {
        return;
      }
      const currentDate = new Date();
      this.selected_date.setHours(
          currentDate.getHours(),
          currentDate.getMinutes(),
          currentDate.getSeconds(),
          currentDate.getMilliseconds()
      );

      const dataFieldsToUpdate = {
        project_id: this.project_id,
        duration_internal: this.internalHoursSum,
        duration_external: this.externalHoursSum,
        quantity: this.quantity,
        text: this.taskText,
        date: this.selected_date.toISOString(),
        task_id: this.tasksTags[0].task.document_id
      };

      this.incrementPendingOperations();
      this.createTaskLog(dataFieldsToUpdate);
    },
    incrementPendingOperations() {
      this.isLoading = true;
      this.pendingOperations += 1;
    },
    decrementPendingOperations() {
      this.pendingOperations -= 1;
      if (this.pendingOperations === 0) {
        this.showSuccess(this.$t("messages.all-logs-created"));  // Show success message at the end
        this.onReset();
        this.isLoading = false;
        this.$emit('allLogsCreated');
      }
    },
    createTaskLog(updateData) {
      const functions = firebase.app().functions("europe-west3");
      const insertTaskLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_log";
      insertTaskLogFunction(updateData)
          .then((result) => {
            console.log("create task log result: ", result.data);
            this.savePersonnelLogs();
            this.saveEquipmentsLogs();
            this.saveConsumablesLogs();
            this.decrementPendingOperations();
          })
          .catch((error) => {
            this.showError(this.$t("messages.fast-logs-common-error") + "\n" + error.message);
            this.decrementPendingOperations();
            console.log("Error writing document: ", error);
          });
    },
    savePersonnelLogs() {
      this.personnelLogs.forEach((personalLog) => {
        if (!this.validatePersonalLogForm(personalLog)) return;
        this.incrementPendingOperations();

        const currentDate = new Date();
        this.selected_date.setHours(
            currentDate.getHours(),
            currentDate.getMinutes(),
            currentDate.getSeconds(),
            currentDate.getMilliseconds()
        );

        const dataFieldsToUpdate = {
          project_id: this.project_id,
          quantity: personalLog.personalWorkingHour + personalLog.personalWorkingMinutes / 60,
          date: this.selected_date.toISOString(),
          personal_id: personalLog.personnelTags[0].personal.document_id
        };

        console.log("creating new personal worklog: ", dataFieldsToUpdate);
        this.createPersonalWorklog(dataFieldsToUpdate);
      });
    },
    createPersonalWorklog(updateData) {
      const functions = firebase.app().functions("europe-west3");
      const insertPersonalWorklogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_log";
      insertPersonalWorklogFunction(updateData)
          .then((result) => {
            console.log("create personal worklog result: ", result.data);
            this.decrementPendingOperations();
          })
          .catch((error) => {
            this.showError(this.$t("messages.fast-logs-common-error") + "\n" + error.message);
            this.decrementPendingOperations();
            console.log("Error writing document: ", error);
          });
    },
    saveEquipmentsLogs() {
      this.equipmentLogs.forEach((equipmentLog) => {
        if (!this.validateEquipmentLogForm(equipmentLog)) return;
        this.incrementPendingOperations();

        const currentDate = new Date();
        this.selected_date.setHours(
            currentDate.getHours(),
            currentDate.getMinutes(),
            currentDate.getSeconds(),
            currentDate.getMilliseconds()
        );

        const dataFieldsToUpdate = {
          project_id: this.project_id,
          quantity: equipmentLog.quantityDay,
          date: this.selected_date.toISOString(),
          equipment_id: equipmentLog.equipmentsTags[0].equipment.document_id
        };

        console.log("creating new equipment log: ", dataFieldsToUpdate);
        this.createEquipmentLog(dataFieldsToUpdate);
      });
    },
    createEquipmentLog(updateData) {
      const functions = firebase.app().functions("europe-west3");
      const insertEquipmentLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_log";
      insertEquipmentLogFunction(updateData)
          .then((result) => {
            console.log("create equipment log result: ", result.data);
            this.decrementPendingOperations();
          })
          .catch((error) => {
            this.showError(this.$t("messages.fast-logs-common-error") + "\n" + error.message);
            this.decrementPendingOperations();
            console.log("Error writing document: ", error);
          });
    },
    saveConsumablesLogs() {
      this.consumableLogs.forEach((consumableLog) => {
        if (!this.validateConsumableLogForm(consumableLog)) return;
        this.incrementPendingOperations();

        const currentDate = new Date();
        this.selected_date.setHours(
            currentDate.getHours(),
            currentDate.getMinutes(),
            currentDate.getSeconds(),
            currentDate.getMilliseconds()
        );

        const dataFieldsToUpdate = {
          project_id: this.project_id,
          quantity: consumableLog.quantity,
          date: this.selected_date.toISOString(),
          consumable_id: consumableLog.consumablesTags[0].consumable.document_id
        };

        console.log("creating new consumable log: ", dataFieldsToUpdate);
        this.createConsumableLog(dataFieldsToUpdate);
      });
    },
    createConsumableLog(updateData) {
      const functions = firebase.app().functions("europe-west3");
      const insertConsumableLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_log";
      insertConsumableLogFunction(updateData)
          .then((result) => {
            console.log("create consumable log result: ", result.data);
            this.decrementPendingOperations();
          })
          .catch((error) => {
            this.showError(this.$t("messages.fast-logs-common-error") + "\n" + error.message);
            this.decrementPendingOperations();
            console.log("Error writing document: ", error);
          });
    }
  },
  mounted() {
    this.readTasksData();
    this.readPersonnelData();
    this.readEquipmentsData();
    this.readConsumablesData();
  },
  computed: {
    internalHoursSum() {
      return this.personnelLogs.filter(personalEntry => personalEntry.personnelTags.length > 0 && personalEntry.personnelTags[0].personal.group === "internal")
          .reduce((sum, personalEntry) => sum + (personalEntry.personalWorkingHour + personalEntry.personalWorkingMinutes / 60), 0);
    },
    externalHoursSum() {
      return this.personnelLogs.filter(personalEntry => personalEntry.personnelTags.length > 0 && personalEntry.personnelTags[0].personal.group === "external")
          .reduce((sum, personalEntry) => sum + (personalEntry.personalWorkingHour + personalEntry.personalWorkingMinutes / 60), 0);
    },
    quantityTitle() {
      let unit, unitKey;
      if (this.tasksTags[0] === undefined || this.tasksTags[0] == null
          || this.tasksTags[0].task.meta.estimation === undefined || this.tasksTags[0].task.meta.estimation == null) {
        return this.$t('task-log-view.quantity');
      } else {
        unit = this.tasksTags[0].task.unit;
      }
      unitKey = this.quantityUnits.find(obj => {
        return obj.id === unit
      });
      return this.$t('task-log-view.quantity') + ((unitKey === undefined || unitKey === "") ? "" : " (" + this.$t(unitKey.translationKey) + ")");
    },
    remainingQuantity() {
      let tag;
      let totalQuantity = 0;
      let totalLoggedQuantity = 0;
      let curLogSavedQuantity = 0;
      if (this.tasksTags[0] === undefined || this.tasksTags[0] == null) {
        return '';
      }
      tag = this.tasksTags[0].task;
      if (tag.meta.estimation === undefined || tag.meta.estimation == null) {
        return;
      }
      totalLoggedQuantity = tag.logged_quantities;
      totalQuantity = tag.meta.estimation.quantity;
      let remaining = (totalLoggedQuantity - totalQuantity - curLogSavedQuantity + this.quantity).toFixed(3);
      return ((remaining > 0) ? this.$t('project-logs.exceeding') : this.$t('project-logs.remaining')) + ": " + Math.abs(remaining);
    }
  }
}
</script>

<style scoped>
hr {
  margin: 1rem 0; /* Customize the spacing if needed */
}
</style>